import { Tab, TabList, makeStyles, Text, tokens } from '@fluentui/react-components';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ErrorState,
  FreshnessChip,
  LoadingState,
  PageHeader,
  SectionCard,
  StatusPill,
  type StatusKind,
} from '@/design-system';
import { spacing } from '@/theme/tokens';
import type { ResourceCriticality, ResourceHealth } from '@/api/types';
import { useEnvStore } from '@/env/envStore';
import { HUB_ENV_FULL_LABEL } from '@/env/types';

import { useDashboardHealth } from './api';
import { HealthTrendsPage } from './HealthTrendsPage';
import { DashboardWindowPicker, type DashboardWindowDays } from './components/DashboardWindowPicker';
import { ResourceCard } from './components/ResourceCard';

const DEFAULT_WINDOW_DAYS: DashboardWindowDays = 30;

const WINDOW_LABEL: Record<DashboardWindowDays, string> = {
  1: '24h',
  7: '7d',
  30: '30d',
};

const STATUS_TO_KIND: Record<ResourceHealth['status'], StatusKind> = {
  Healthy: 'healthy',
  Degraded: 'degraded',
  Unhealthy: 'unhealthy',
  Unknown: 'unknown',
};

const CRITICALITY_LABEL: Record<ResourceCriticality, string> = {
  Critical: 'Critical',
  Important: 'Important',
  Informational: 'Informational',
};

const useStyles = makeStyles({
  hero: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: spacing.l,
    marginBottom: spacing.xxxl,
  },
  heroCard: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: spacing.l,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  heroValue: {
    fontSize: '32px',
    lineHeight: '36px',
    fontWeight: 600,
    fontFeatureSettings: '"tnum" 1',
  },
  heroLabel: {
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.l,
    marginBottom: spacing.xxxl,
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: spacing.l,
  },
  // Trailing slot stacks the picker on top of the "last updated" line so
  // both stay readable on narrow viewports. On wide ones the PageHeader's
  // own flex layout puts the trailing block on the right edge.
  trailing: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: spacing.xs,
  },
  lastUpdated: {
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
});

export function DashboardPage() {
  const classes = useStyles();
  const env = useEnvStore((s) => s.env);
  // Window state is ephemeral per page-mount (not persisted to
  // localStorage). The dashboard's primary purpose is "what's broken right
  // now" — a sticky 1d picker survives an incident view past relevance.
  // 30d default matches the SLO-tracking expectation.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'application' ? 'application' : searchParams.get('tab') === 'dependency' ? 'dependency' : 'uptime';
  const [windowDays, setWindowDays] = useState<DashboardWindowDays>(DEFAULT_WINDOW_DAYS);
  const tab = initialTab;
  const query = useDashboardHealth(windowDays, tab === 'uptime');

  const tabs = (
    <TabList aria-label="Dashboard health views" selectedValue={tab} onTabSelect={(_, data) => { const next = new URLSearchParams(searchParams); next.set('tab', data.value as string); setSearchParams(next); }}>
      <Tab id="uptime-tab" aria-controls="uptime-panel" value="uptime">Uptime</Tab>
      <Tab id="application-tab" aria-controls="application-panel" value="application">Application health</Tab>
      <Tab id="dependency-tab" aria-controls="dependency-panel" value="dependency">Dependency health</Tab>
    </TabList>
  );

  if (tab === 'application' || tab === 'dependency') {
    return (
      <div>
        <PageHeader
          title={`Fleet health · ${HUB_ENV_FULL_LABEL[env]}`}
          description="Platform uptime, application health, and dependency health."
          trailing={<DashboardWindowPicker value={windowDays} onChange={setWindowDays} />}
        />
        {tabs}
        <div id="uptime-panel" role="tabpanel" aria-labelledby="uptime-tab" hidden />
        <div id="application-panel" role="tabpanel" aria-labelledby="application-tab" hidden={tab !== 'application'}>
          {tab === 'application' ? <HealthTrendsPage kind="application" windowDays={windowDays} embedded /> : null}
        </div>
        <div id="dependency-panel" role="tabpanel" aria-labelledby="dependency-tab" hidden={tab !== 'dependency'}>
          {tab === 'dependency' ? <HealthTrendsPage kind="dependency" windowDays={windowDays} embedded /> : null}
        </div>
      </div>
    );
  }

  const grouped = query.data?.resources.reduce<Record<ResourceCriticality, ResourceHealth[]>>(
    (acc, r) => {
      acc[r.criticality].push(r);
      return acc;
    },
    { Critical: [], Important: [], Informational: [] },
  );

  if (query.isLoading || query.error || !query.data || !grouped) {
    return (
      <div>
        <PageHeader
          title={`Fleet health · ${HUB_ENV_FULL_LABEL[env]}`}
          description="Platform uptime, application health, and dependency health."
          trailing={<DashboardWindowPicker value={windowDays} onChange={setWindowDays} />}
        />
        {tabs}
        <div id="application-panel" role="tabpanel" aria-labelledby="application-tab" hidden />
        <div id="dependency-panel" role="tabpanel" aria-labelledby="dependency-tab" hidden />
        <div id="uptime-panel" role="tabpanel" aria-labelledby="uptime-tab">
          {query.isLoading ? <LoadingState label={`Loading ${HUB_ENV_FULL_LABEL[env]} health…`} /> : null}
          {query.error ? <ErrorState title="Could not load Uptime" description={query.error.message} retry={() => void query.refetch()} /> : null}
        </div>
      </div>
    );
  }

  const snapshot = query.data;
  const overallKind = STATUS_TO_KIND[snapshot.overallStatus];

  return (
    <div>
      <PageHeader
        title={`Fleet health · ${HUB_ENV_FULL_LABEL[env]}`}
        description="Rollup of every critical resource the CareDocs EHR app depends on. Auto-refreshes every minute."
        trailing={
          <div className={classes.trailing}>
            <DashboardWindowPicker value={windowDays} onChange={setWindowDays} />
            <FreshnessChip
              asOfMs={snapshot.observedAt}
              className={classes.lastUpdated}
              isRefreshing={query.isFetching && !query.isLoading}
              onRefresh={() => {
                void query.refetch();
              }}
            />
          </div>
        }
      />

      {tabs}
      <div id="application-panel" role="tabpanel" aria-labelledby="application-tab" hidden />
      <div id="dependency-panel" role="tabpanel" aria-labelledby="dependency-tab" hidden />
      <div id="uptime-panel" role="tabpanel" aria-labelledby="uptime-tab">
      <div className={classes.hero}>
        <div className={classes.heroCard}>
          <Text size={100} weight="semibold" className={classes.heroLabel}>
            Overall status
          </Text>
          <StatusPill kind={overallKind} />
        </div>
        <div className={classes.heroCard}>
          <Text size={100} weight="semibold" className={classes.heroLabel}>
            Fleet uptime ({WINDOW_LABEL[windowDays]})
          </Text>
          <span className={classes.heroValue}>{snapshot.fleetUptimePct.toFixed(2)}%</span>
        </div>
        <div className={classes.heroCard}>
          <Text size={100} weight="semibold" className={classes.heroLabel}>
            Resources monitored
          </Text>
          <span className={classes.heroValue}>{snapshot.resources.length.toString()}</span>
        </div>
      </div>

      {(['Critical', 'Important', 'Informational'] as const).map((tier) => {
        const items = grouped[tier];
        if (items.length === 0) {
          return null;
        }
        return (
          <div key={tier} className={classes.group}>
            <SectionCard title={CRITICALITY_LABEL[tier]} description={`${String(items.length)} resources`}>
              <div className={classes.cardGrid}>
                {items.map((r) => (
                  <ResourceCard key={r.id} resource={r} windowLabel={`${WINDOW_LABEL[windowDays]} uptime`} />
                ))}
              </div>
            </SectionCard>
          </div>
        );
      })}
      </div>
    </div>
  );
}
